import {
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { ChangeEvent, useState } from 'react';
import HelpIcon from 'react-feather/dist/icons/help-circle';
import { Entity, EntityClass } from '../../../../../types';
import { JupiterTheme } from '../../../../theme/types';
import Tile from './Tile';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    helpIcon: {
      color: theme.palette.grey[400],
      marginBottom: theme.spacing(1),
    },
    adornment: {
      marginRight: 0,
      marginLeft: theme.spacing(0.5),
    },
    formSection: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(4),
    },
    textField: {
      marginBottom: theme.spacing(2),
      fontWeight: 'normal',
      '&:last-child': {
        marginBottom: 0,
      },
      '& .Mui-required': {
        display: 'flex',
      },
    },
    title: {
      color: theme.palette.primary.dark,
      marginBottom: theme.spacing(1),
    },
    infoSection: {
      marginLeft: theme.spacing(1.5),
    },
    header: {
      display: 'flex',
      marginTop: theme.spacing(4),
      marginBottom: theme.spacing(4.5),
      '& svg': {
        alignSelf: 'flex-start',
        height: 100,
        strokeWidth: 0.5,
        width: 100,
      },
    },
    description: {
      color: theme.palette.coolGrey[600],
    },
    formPaper: {
      display: 'flex',
      flexFlow: 'column',
      padding: theme.spacing(3),
    },
    tooltip: {
      maxWidth: '300px',
    },
  })
);

interface HookProps {
  entity: Entity;
  onUpdateProperties: (key: string, value: any) => void;
  onUpdateManyProperties: (keys: string[], value: any) => void;
  onUpdateEntity: (key: string, propertyKey: string, value: any) => void;
}

interface Props extends HookProps {
  selectedClass: EntityClass;
  selectedClassName: string;
  isIntegrationEntity?: boolean;
}

const nameKey = 'name';
const displayNameKey = 'displayName';
const typeKey = 'entityType';
const typePropertyKey = '_type';

const useEntityDefinition = ({
  entity,
  onUpdateProperties,
  onUpdateManyProperties,
  onUpdateEntity,
}: HookProps) => {
  const [updatedDisplayName, setUpdatedDisplayName] = useState<boolean>(false);

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (updatedDisplayName === false) {
      onUpdateManyProperties([nameKey, displayNameKey], event.target.value);
    } else {
      onUpdateProperties(nameKey, event.target.value);
    }
  };

  const handleDisplayNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    onUpdateProperties(displayNameKey, event.target.value);
    if (updatedDisplayName === false) {
      setUpdatedDisplayName(true);
    }
  };

  const handleTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = entity[typeKey]?.length ? [...entity[typeKey]] : [];
    value[0] = event.target.value || '';
    onUpdateEntity(typeKey, typePropertyKey, value);
  };

  const handleValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    onUpdateProperties(event.target.name, event.target.value);
  };
  return {
    handleNameChange,
    handleDisplayNameChange,
    handleTypeChange,
    handleValueChange,
  };
};

interface formInput {
  label: string;
  key: string;
  description: string;
  rows?: number;
}
const FORM_INPUTS: formInput[] = [
  {
    label: 'Summary',
    key: 'summary',
    description: 'A summary / short description of this entity.',
  },
  {
    label: 'Description',
    key: 'description',
    description: 'An extended description of this entity.',
    rows: 4,
  },
  {
    label: 'Classification',
    key: 'classification',
    description:
      'The sensitivity of the data; should match company data classification scheme',
  },
];

const EntityDefinition = ({
  selectedClassName,
  selectedClass,
  isIntegrationEntity,
  ...rest
}: Props) => {
  const classes = useStyles();
  const {
    handleNameChange,
    handleDisplayNameChange,
    handleTypeChange,
    handleValueChange,
  } = useEntityDefinition(rest);
  return (
    <div className={classes.formSection}>
      <div className={classes.header}>
        <Tile className={selectedClassName} />
        <div className={classes.infoSection}>
          <Typography variant="subtitle1" className={classes.title}>
            {selectedClassName}
          </Typography>
          <Typography variant="body1" className={classes.description}>
            {selectedClass.description}
          </Typography>
        </div>
      </div>
      <Paper className={classes.formPaper}>
        <TextField
          required
          className={classes.textField}
          value={rest.entity.entityType?.[0] || ''}
          onChange={handleTypeChange}
          label={<div>Type</div>}
          id={'type'}
          disabled={isIntegrationEntity}
          InputProps={{
            endAdornment: (
              <InputAdornment
                classes={{ root: classes.adornment }}
                position="start"
              >
                <Tooltip
                  classes={{ tooltip: classes.tooltip }}
                  placement="right"
                  title={
                    'Describes the type of entity/relationship as identified by the data source (often the integration or sometimes manual user input). The _class property is similar to _type but _class refers to a categorization that has been standardized and it is not unique to a single data integration.'
                  }
                >
                  <div>
                    <HelpIcon className={classes.helpIcon} />
                  </div>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          data-testid="EntityDefinition-name"
          className={classes.textField}
          value={rest.entity.properties['name'] || ''}
          id={'name'}
          onChange={handleNameChange}
          label={<div>Name</div>}
          disabled={isIntegrationEntity}
          InputProps={{
            endAdornment: (
              <InputAdornment
                classes={{ root: classes.adornment }}
                position="start"
              >
                <Tooltip
                  classes={{ tooltip: classes.tooltip }}
                  placement="right"
                  title={'Name of this entity'}
                >
                  <div>
                    <HelpIcon className={classes.helpIcon} />
                  </div>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          required
          className={classes.textField}
          value={rest.entity.properties['displayName'] || ''}
          onChange={handleDisplayNameChange}
          label={<div>Display Name</div>}
          id={'displayName'}
          InputProps={{
            endAdornment: (
              <InputAdornment
                classes={{ root: classes.adornment }}
                position="start"
              >
                <Tooltip
                  classes={{ tooltip: classes.tooltip }}
                  placement="right"
                  title={
                    "Display name, e.g. a person's preferred name or an AWS account alias"
                  }
                >
                  <div>
                    <HelpIcon className={classes.helpIcon} />
                  </div>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
        {FORM_INPUTS.map((input) => (
          <TextField
            key={input.key}
            id={input.key}
            name={input.key}
            label={<div>{input.label}</div>}
            className={classes.textField}
            value={rest.entity.properties[input.key] || ''}
            onChange={handleValueChange}
            multiline={!!input.rows}
            rows={input.rows}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  classes={{ root: classes.adornment }}
                  position="start"
                >
                  <Tooltip
                    classes={{ tooltip: classes.tooltip }}
                    placement="right"
                    title={input.description}
                  >
                    <div>
                      <HelpIcon className={classes.helpIcon} />
                    </div>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        ))}
      </Paper>
    </div>
  );
};

export default EntityDefinition;
